import {
	Bell,
	BookCheck,
	FileStack,
	Frown,
	LayoutDashboard,
	NotebookText,
	School,
	ShieldAlert,
	ShieldCheck,
	SquareCheck,
	Syringe,
	User,
	UserCheck,
} from "lucide-react";

import { useTabCountQuery } from "../../api/repApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { usePendingChangesContext } from "../../contexts/pendingChangesContext";
import { useSettingsQuery } from "../../helpers/queryHooks";
import { tabs } from "../../string";

export function useStates() {
	const { repId } = useRepContext();
	const [settings] = useSettingsQuery();

	const newRep = !repId;

	const onboarding = {
		id: "onboarding",
		label: "Onboarding",
		color: "bg-blue-500",
		lightColor: "bg-blue-50",
		textColor: "text-blue-500",
		hoverColor: "hover:bg-blue-50",
		activeColor: "bg-blue-100",
		activeTextColor: "text-blue-700",
		borderColor: "border-blue-500",
	};

	const sales = {
		id: "sales",
		label: "Sales",
		color: "bg-green-500",
		lightColor: "bg-green-50",
		textColor: "text-green-500",
		hoverColor: "hover:bg-green-50",
		activeColor: "bg-green-100",
		activeTextColor: "text-green-700",
		borderColor: "border-green-500",
	};

	const all = {
		id: "all",
		label: "All",
		color: "bg-purple-500",
		lightColor: "bg-purple-50",
		textColor: "text-purple-500",
		hoverColor: "hover:bg-purple-50",
		activeColor: "bg-purple-100",
		activeTextColor: "text-purple-700",
		borderColor: "border-purple-500",
	};
	const tabSettingSections = settings?.tabSettings?.sections ?? [];
	if (newRep) return [onboarding];

	const hasOnboarding = tabSettingSections.includes("onboarding");
	const hasSales = tabSettingSections.includes("sales");
	const hasAll = tabSettingSections.includes("all");

	return [hasOnboarding && onboarding, hasSales && sales, hasAll && all].filter(Boolean);
}

export function usePages() {
	const { repId } = useRepContext();
	const countsQuery = useTabCountQuery(repId);
	const { isItemChanged } = usePendingChangesContext();
	const [settings] = useSettingsQuery();

	const tabSettings = {
		showBgCheck: settings.tabSettings.backgroundCheck && settings.onboarding,
		showDrugTest: settings.tabSettings.drugTest && settings.onboarding,
		showTraining: settings.tabSettings.training && settings.onboarding,
		showTrainerCert: settings.tabSettings.trainerCertification,
		showDocumentation: settings.tabSettings.documentation && settings.onboarding,
		showComplaints: settings.tabSettings.complaints,
		showEnrollmentValidations: settings.tabSettings.enrollmentValidations,
		showDiscipline: settings.tabSettings.disciplines,
		showOnboardingAudit: settings.tabSettings.onboardingAudit,
		showFieldAudit: settings.tabSettings.fieldAudit,
		showOnboardingStates: settings.tabSettings.onboardingStates,
		showIdValidation: settings.features?.["id_verification"],
	};

	const newRep = !repId;

	const summary = {
		id: tabs.summary,
		label: "Summary",
		icon: LayoutDashboard,
		changes: false,
		count: 0,
		hidden: false,
		disabled: newRep,
	};

	const personalInfo = {
		id: tabs.personalInfo,
		label: "Personal Info",
		icon: User,
		changes: isItemChanged("rep"),
		count: 0,
		hidden: false,
		disabled: false,
	};

	const bgChecks = {
		id: tabs.bgChecks,
		label: "Background Checks",
		icon: UserCheck,
		changes: isItemChanged("bgc"),
		count: countsQuery.data?.backgroundChecks,
		hidden: !tabSettings.showBgCheck,
		disabled: newRep,
	};

	const drugTests = {
		id: tabs.drugTests,
		label: "Drug Tests",
		icon: Syringe,
		changes: isItemChanged("drugTest"),
		count: countsQuery.data?.drugTests,
		hidden: !tabSettings.showDrugTest,
		disabled: newRep,
	};

	const training = {
		id: tabs.training,
		label: "Training",
		icon: School,
		changes: isItemChanged("repTraining"),
		count: countsQuery.data?.trainings,
		hidden: !tabSettings.showTraining,
		disabled: newRep,
	};

	const trainerCert = {
		id: tabs.trainerCert,
		label: "Trainer Certification",
		icon: Bell,
		changes: isItemChanged("trainerCertification"),
		count: 0,
		hidden: !tabSettings.showTrainerCert,
		disabled: newRep,
	};

	const documentation = {
		id: tabs.documentation,
		label: "Documentation",
		icon: NotebookText,
		changes: isItemChanged("documentation"),
		hidden: !tabSettings.showDocumentation,
		disabled: newRep,
	};

	const onboardingStates = {
		id: tabs.onboardingStates,
		label: "Onboarding States",
		icon: FileStack,
		changes: false,
		hidden: !tabSettings.showOnboardingStates,
		disabled: newRep,
	};

	const complaints = {
		id: tabs.complaints,
		label: "Customer Incidents",
		icon: Frown,
		changes: isItemChanged("complaint"),
		count: countsQuery.data?.complaints,
		hidden: !tabSettings.showComplaints,
		disabled: newRep,
	};

	const eves = {
		id: tabs.eves,
		label: "Enrollment Validations",
		icon: BookCheck,
		changes: isItemChanged("eve"),
		count: countsQuery.data?.enrollmentValidations,
		hidden: !tabSettings.showEnrollmentValidations,
		disabled: newRep,
	};

	const idValidation = {
		id: tabs.idValidation,
		label: "Id Validation",
		icon: ShieldCheck,
		changes: false,
		count: 0,
		hidden: !tabSettings.showIdValidation,
		disabled: newRep,
	};

	const repDisciplines = {
		id: tabs.repDisciplines,
		label: "Disciplines",
		icon: ShieldAlert,
		changes: isItemChanged("repDiscipline"),
		count: countsQuery.data?.disciplines,
		hidden: !tabSettings.showDiscipline,
		disabled: newRep,
	};

	const onboardingAudits = {
		id: tabs.onboardingAudits,
		label: "Onboarding Audits",
		icon: SquareCheck,
		changes: isItemChanged("repOnboardingAudit"),
		count: countsQuery.data?.onboardingAudits,
		hidden: !tabSettings.showOnboardingAudit,
		disabled: newRep,
	};

	const fieldAudits = {
		id: tabs.fieldAudits,
		label: "Field Audits",
		icon: ShieldCheck,
		changes: isItemChanged("repFieldAudit"),
		count: countsQuery.data?.fieldAudits,
		hidden: !tabSettings.showFieldAudit,
		disabled: newRep,
	};

	return {
		onboarding: [
			summary,
			personalInfo,
			bgChecks,
			drugTests,
			training,
			trainerCert,
			documentation,
			onboardingStates,
			complaints,
			eves,
			idValidation,
			repDisciplines,
			onboardingAudits,
			fieldAudits,
		],
		sales: [summary, personalInfo, complaints, eves, repDisciplines, onboardingAudits, fieldAudits],
		all: [
			summary,
			personalInfo,
			bgChecks,
			drugTests,
			training,
			trainerCert,
			documentation,
			onboardingStates,
			complaints,
			eves,
			idValidation,
			repDisciplines,
			onboardingAudits,
			fieldAudits,
		],
	};
}
